import type { ReactElement } from 'react';
import { forwardRef, useCallback, useEffect, useState } from 'react';

import { BpkCardV2, CARD_V2_VARIANTS } from '@skyscanner-internal/backpack-web/bpk-component-card';
import {
  BpkBox,
  BpkFlex,
  BpkSpacing,
  BpkVessel,
} from '@skyscanner-internal/backpack-web/bpk-component-layout';
import BpkPressable, {
  PRESSABLE_RADIUS,
} from '@skyscanner-internal/backpack-web/bpk-component-pressable';
import BpkText, { TEXT_STYLES } from '@skyscanner-internal/backpack-web/bpk-component-text';
import { ANALYTICS_EVENTS } from '@web-platform/shared-universal-constants-tracking';
import { trackInteractionEvent } from '@web-platform/shared-universal-tracking/src/trackInteractionEvent/trackInteractionEvent';
import { GLOBAL } from 'saddlebag-browser';
import { logOperationalEvent } from 'saddlebag-logger';

import { CardIcon } from './CardIcon/CardIcon';

import type { URL } from '@web-platform/shared-universal-types-utils/src/utils';

import STYLES from './GlobalNavigationFallbackCard.module.scss';

type TextStyle = (typeof TEXT_STYLES)[keyof typeof TEXT_STYLES];
const $window = GLOBAL.getWindow();
export type OwnProps = {
  title: string;
  type: string;
  pageName: string;
  CTAUrl: URL;
  index: number;
  label: string;
  onClick: () => Promise<void>;
};

/*
  A design requirement is that if the title of a navcard will exceed two lines, we should
  use small text instead of the default text size.
  We measure the rendered text element via a callback ref. If the text height exceeds two
  line-heights, we switch to a smaller text style.
*/
const getTextStyleForElement = (element: Element): TextStyle => {
  const rect = element.getClientRects()[0];

  if (rect?.height) {
    const lineHeight = parseInt($window.getComputedStyle(element).lineHeight, 10);
    const numberOfLinesInCard = rect.height / lineHeight;

    if (numberOfLinesInCard > 2) {
      return TEXT_STYLES.label2;
    }
  }

  return TEXT_STYLES.label1;
};

export const GlobalNavigationFallbackCard = forwardRef<HTMLDivElement | null, OwnProps>(
  ({ CTAUrl, index, label, onClick, pageName, title, type }, ref): ReactElement => {
    useEffect(() => {
      const payload = {
        strings: {
          title,
          type,
          CTAUrl,
          pageName,
          label,
        },
        numbers: {
          index,
        },
      };
      trackInteractionEvent({
        name: ANALYTICS_EVENTS.INTERACTION.GLOBAL_NAV_FALLBACK_CARD_LOADED.NAME,
        ...payload,
      });

      logOperationalEvent({
        eventName: ANALYTICS_EVENTS.INTERACTION.GLOBAL_NAV_FALLBACK_CARD_LOADED.NAME,
        ...payload.strings,
        ...payload.numbers,
      });
    }, [title, type, CTAUrl, pageName, label, index]);
    const [textStyle, setTextStyle] = useState<TextStyle>('label-1');
    const measureTextRef = useCallback(
      (element: HTMLElement | null) => {
        if (element) {
          setTextStyle(getTextStyleForElement(element));
        }
      },
      // title is included to re-measure when the text content changes
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [title],
    );
    return (
      <BpkVessel
        className={STYLES.container}
        data-testid={`${type}-global-navigation-fallback-card`}
        ref={ref}
      >
        <BpkPressable
          as="a"
          role="link"
          radius={PRESSABLE_RADIUS.md}
          fill
          href={CTAUrl || '#'}
          onClick={onClick}
        >
          <BpkCardV2.Root variant={CARD_V2_VARIANTS.homepageNav}>
            <BpkFlex
              width="full"
              minWidth={{ base: '8.75rem', desktop: '13.375rem' }}
              minHeight={{ base: '6.75rem', desktop: '4.5rem' }}
              padding={BpkSpacing.Base}
              direction={{ base: 'column', desktop: 'row' }}
              justify={{ base: 'space-between', desktop: 'flex-start' }}
              align={{ base: 'normal', desktop: 'center' }}
            >
              <CardIcon type={type} />
              <BpkBox as="span" ref={measureTextRef}>
                <BpkText textStyle={textStyle} className={STYLES.truncate}>
                  {title}
                </BpkText>
              </BpkBox>
            </BpkFlex>
          </BpkCardV2.Root>
        </BpkPressable>
      </BpkVessel>
    );
  },
);
